import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useConfig } from "../../../hooks/use-config";
import { addFilesToProject, addInstructionToProject, deleteFileFromProject, fetchProjectFiles, fetchProjectFilesPayload, fetchProjects, fetchProjectsPage, projectQueryParams, ConvertedProjectResponse } from "../services/projects-services";
import { useToast } from "../../../hooks/use-toast";
import { useChatStore } from "../../../state/store/chat/use-chat-store";

export const useProjects = (queryParams: projectQueryParams) => {
    const { configurationSettings } = useConfig();
    return useQuery({
        queryKey: ["getProjects", queryParams],
        queryFn: () => fetchProjects(queryParams, configurationSettings),
    });
}

/** Paged list with the total count, for the /projects page.
 *  Separate key from "getProjects" because the cached shape is not Project[]. */
export const useProjectsPage = (queryParams: projectQueryParams) => {
    const { configurationSettings } = useConfig();
    return useQuery({
        queryKey: ["getProjectsPage", queryParams],
        queryFn: () => fetchProjectsPage(queryParams, configurationSettings),
    });
}

/** Projects for pickers: the sidebar list (kept live on create/rename/delete),
 *  or the first fetched page until the sidebar has hydrated it. */
export const useProjectOptions = () => {
    const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
    const { data: fetchedProjects } = useProjects({
        page: 1,
        pageSize: 50,
        order: 'desc',
        searchQuery: '',
    });
    return sidebarProjectsList.length ? sidebarProjectsList : fetchedProjects ?? [];
}



export const useAddFilesToProject = (projectId: string) => {
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["addFilesToProject", projectId],

        mutationFn: (file: File) =>
            addFilesToProject(configurationSettings, projectId, file),

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["projectFiles", projectId],
            });
        },
    });
};


export const useFetchProjectFiles = (payload: fetchProjectFilesPayload, options?: { enabled?: boolean }) => {
    const { configurationSettings } = useConfig();
    return useQuery({
        queryKey: ["projectFiles", payload.projectId, payload.page],
        queryFn: () => fetchProjectFiles(configurationSettings, payload),
        enabled: options?.enabled !== false,
    });
}

export const useDeleteFileFromProject = (projectId: string) => {
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationKey: ["deleteFileFromProject", projectId],
        mutationFn: (fileIds: string[]) => deleteFileFromProject(configurationSettings, projectId, fileIds),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["projectFiles", projectId],
            });
            toast({
                title: 'File deleted successfully',
                description: 'The file has been deleted from the project',
                variant: 'success',
            });
        },
    });
}



export const useAddInstructionToProject = (projectId: string) => {
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();
    return useMutation<ConvertedProjectResponse, Error, string>({
        mutationKey: ["addInstructionToProject", projectId],
        mutationFn: (instruction: string) => addInstructionToProject(configurationSettings, projectId, instruction),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["project", projectId],
            });
        },
    });
}
